/**
 * The Agents section's left panel, like the Data Model's Quick Nav: an Overview link, then a
 * tree of the tenant's agents (each expanding to its steps) and connections (grouped by
 * connector). Rows navigate. Sections and agents collapse; the one you're on stays open.
 */
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bot, ChevronDown, ChevronRight, LayoutList, Loader2, Plug, Plus, Search, Workflow } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { CONNECTORS, type Connection } from './catalog';
import { AGENTS_PATH, connectionPath, teamPath, useConnections, useTeams } from './data';

const rowClass = (active: boolean) =>
  `w-full text-left px-2 py-1 rounded-md text-xs flex items-center gap-2 transition-colors select-none ${
    active ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50 text-foreground'
  }`;

function Section({ title, count, open, onToggle, listPath, addPath, children }: {
  title: string;
  count: number;
  open: boolean;
  onToggle: () => void;
  listPath: string;
  addPath: string;
  children: React.ReactNode;
}) {
  const { pathname } = useLocation();
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <div>
      <div className={`flex items-center gap-1 mb-2 text-[11px] font-medium uppercase tracking-wide ${pathname === listPath ? 'text-foreground' : 'text-muted-foreground'}`}>
        <button type="button" onClick={onToggle} aria-expanded={open} className="flex-1 min-w-0 flex items-center gap-1 select-none hover:text-foreground">
          <Chevron className="h-3 w-3 shrink-0" />
          <span className="truncate">{title}</span>
        </button>
        <span className="tabular-nums normal-case">{count}</span>
        <Link to={addPath} title={`New ${title.toLowerCase().replace(/s$/, '')}`} aria-label={`New ${title.toLowerCase().replace(/s$/, '')}`} className="shrink-0 p-0.5 rounded hover:bg-accent/50 hover:text-foreground">
          <Plus className="h-3 w-3" />
        </Link>
      </div>
      {open && <div className="space-y-1">{children}</div>}
    </div>
  );
}

export default function AgentsNav() {
  const { pathname } = useLocation();
  const [query, setQuery] = useState('');
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const teams = useTeams();
  const connections = useConnections();

  const q = query.toLowerCase().trim();
  const match = (s: string) => !q || s.toLowerCase().includes(q);
  const teamList = (teams.data?.teams ?? [])
    .filter((t) => match(t.name) || t.members.some((m) => match(m.name)))
    .sort((a, b) => a.name.localeCompare(b.name));
  const groups: Record<string, Connection[]> = {};
  for (const c of connections.data?.connections ?? []) if (match(c.name) || match(c.connector)) (groups[c.connector] ??= []).push(c);
  const byConnector = Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));

  // What's open: what you toggled, else what you're on (and everything, while searching).
  const onTeam = pathname.match(/^\/agents\/teams\/([^/]+)/)?.[1];
  const onConnection = pathname.match(/^\/agents\/connections\/([^/]+)/)?.[1];
  const isOpen = (key: string, current: boolean) => overrides[key] ?? (current || !!q);
  const toggle = (key: string, current: boolean) => setOverrides((o) => ({ ...o, [key]: !isOpen(key, current) }));
  const agentsOpen = isOpen('agents', true);
  const connectionsOpen = isOpen('connections', true);

  return (
    <div className="w-80 border-r border-border flex flex-col flex-shrink-0 h-full overflow-hidden">
      <div className="flex-1 min-h-0 overflow-auto p-4">
        <div className="flex items-center gap-4 mb-3">
          <SidebarTrigger className="md:hidden h-5 w-5 shrink-0" />
          <h2 className="text-xs font-semibold text-foreground shrink-0">Agents</h2>
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
            <Input type="text" placeholder="Search..." value={query} onChange={(e) => setQuery(e.target.value)} className="h-7 pl-6 text-xs" />
          </div>
        </div>
        <div className="mb-4">
          <Link to={AGENTS_PATH} className={rowClass(pathname === AGENTS_PATH)}>
            <LayoutList className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="truncate">Overview</span>
          </Link>
        </div>
        {teams.isLoading || connections.isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-4">
            <Section
              title="Agents"
              count={teamList.length}
              open={agentsOpen}
              onToggle={() => toggle('agents', true)}
              listPath={AGENTS_PATH}
              addPath={`${AGENTS_PATH}/teams/new`}
            >
              {teamList.map((t) => {
                const key = `team:${t.id}`;
                const open = isOpen(key, onTeam === t.id);
                const Chevron = open ? ChevronDown : ChevronRight;
                return (
                  <div key={t.id}>
                    <div className={`${rowClass(onTeam === t.id)} pl-1`}>
                      <button type="button" aria-label={open ? 'Collapse' : 'Expand'} onClick={() => toggle(key, onTeam === t.id)} className="shrink-0">
                        <Chevron className="h-3 w-3 text-muted-foreground" />
                      </button>
                      <Link to={teamPath(t.id!)} className="flex-1 min-w-0 flex items-center gap-2">
                        <Bot className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        <span className="truncate">{t.name}</span>
                      </Link>
                    </div>
                    {open && (
                      <div className="ml-5 mt-1 space-y-1">
                        {t.members.map((m) => (
                          <Link key={m.id ?? m.name} to={`${teamPath(t.id!, 'steps')}#step-${m.id}`} className={rowClass(false)}>
                            <Workflow className="h-3 w-3 text-muted-foreground" />
                            <span className="truncate">{m.name}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </Section>
            <Section
              title="Connections"
              count={byConnector.reduce((n, [, cs]) => n + cs.length, 0)}
              open={connectionsOpen}
              onToggle={() => toggle('connections', true)}
              listPath={`${AGENTS_PATH}/connections`}
              addPath={`${AGENTS_PATH}/connections`}
            >
              {byConnector.map(([connector, list]) => (
                <div key={connector}>
                  <div className="px-2 pt-1 text-[11px] text-muted-foreground">{CONNECTORS[connector]?.label ?? connector}</div>
                  {list.map((c) => (
                    <Link key={c.id} to={connectionPath(c.id)} className={rowClass(onConnection === c.id)}>
                      <Plug className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="truncate">{c.name}</span>
                    </Link>
                  ))}
                </div>
              ))}
            </Section>
          </div>
        )}
      </div>
    </div>
  );
}
